import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { AxiosError } from 'axios';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { PointAccount } from '@/models/point-account.model';
import { getPointAccount } from '@/services/point-account.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import PointAccountForm from './point-account-form';
import PointAccountBalance from './point-account-balance';
import PointAccountAssignmentList from './point-account-assignment-list';
import PointEarnRuleList from './point-earn-rule-list';
import PointAccountTransactionList from './point-account-transaction-list';

const Page = () => {
  const [activeTab, setActiveTab] = useState('point-account-form');
  const [formObject, setFormObject] = useState<PointAccount | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [ledgerKey, setLedgerKey] = useState(0);
  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;

  useEffect(() => {
    if (id) {
      fetchData(true);
    } else {
      setFormObject(undefined);
      setActiveTab('point-account-form');
    }
  }, [id]);

  const fetchData = async (showLoader = false) => {
    if (!id) return;
    if (showLoader) setLoading(true);
    try {
      const result = await getPointAccount(id);
      if (result.success) setFormObject(result.result);
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching point account: ${errorModel?.errorMessage}`);
      nav('/point-accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleMoneyMoved = () => {
    fetchData();
    setLedgerKey(k => k + 1);
  };

  const newMode = pageState == PageState.New;

  return (
    <Panel
      header={
        <PageHeader title={!newMode && formObject ? `Edit Point Account: ${formObject.name}` : 'New Point Account'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/point-accounts">Point Accounts</Breadcrumb.Item>
            <Breadcrumb.Item active>{newMode ? 'New' : 'Edit'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <Nav appearance="subtle" activeKey={activeTab} onSelect={key => setActiveTab(key)}>
        <Nav.Item eventKey="point-account-form">Detail</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="point-account-assignments">Assignments</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="point-account-rules">Earn Rules</Nav.Item>
        <Nav.Item disabled={newMode} eventKey="point-account-ledger">Ledger</Nav.Item>
      </Nav>
      {loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'point-account-form' && (
            <>
              {formObject && <PointAccountBalance account={formObject} onChanged={handleMoneyMoved} />}
              <PointAccountForm formObject={formObject} pageState={pageState} onSaved={() => fetchData()} />
            </>
          )}
          {activeTab == 'point-account-assignments' && formObject &&
            <PointAccountAssignmentList account={formObject} onChanged={() => fetchData()} />}
          {activeTab == 'point-account-rules' && id && <PointEarnRuleList pointAccountId={id} />}
          {activeTab == 'point-account-ledger' && id && <PointAccountTransactionList pointAccountId={id} reloadKey={ledgerKey} />}
        </>
      }
    </Panel>
  );
};

export default Page;
